/**
 * Two-pane mega menu: hover or click to swap the right-hand panel (Ruohonjuuri-style).
 * Click mode: first click opens a category; second click on the same row returns to promo view.
 */
(function () {
  const READY = 'dataMegaMenuReady';

  function findPanelRows(root) {
    return Array.from(root.querySelectorAll('[data-mega-second-panel]'));
  }

  function findPrimaryItems(root) {
    return Array.from(root.querySelectorAll('[data-mega-primary-item]'));
  }

  function rowHasPanel(root, rowNumber) {
    return findPanelRows(root).some((panel) => parseInt(panel.dataset.megaRow, 10) === rowNumber);
  }

  function setPanelsVisibility(root, rowNumber, visible) {
    const panels = findPanelRows(root);
    panels.forEach((panel) => {
      const pr = parseInt(panel.dataset.megaRow, 10);
      const active = visible && pr === rowNumber;
      panel.classList.toggle('is-active', active);
      if (active) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });
  }

  function setLeftActive(root, rowNumber, active) {
    findPrimaryItems(root).forEach((item) => {
      const row = parseInt(item.dataset.megaRow, 10);
      const hasPanel = item.dataset.hasPanel === 'true';
      const isActive = active && hasPanel && row === rowNumber;
      item.classList.toggle('mega-menu__primary-item--active', isActive);

      const control = item.querySelector('[data-mega-trigger], .mega-menu__primary-link[aria-controls]');
      if (control) {
        control.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      }
    });
  }

  function updateCollapseClass(root, collapsePromos, branchOpen, rowNumber) {
    const hasPanel = branchOpen && rowHasPanel(root, rowNumber);
    root.classList.toggle('mega-menu--collapse-promos', collapsePromos && hasPanel);
  }

  function activateHoverRow(root, rowNumber, collapsePromos) {
    setLeftActive(root, rowNumber, true);
    setPanelsVisibility(root, rowNumber, true);
    updateCollapseClass(root, collapsePromos, true, rowNumber);
  }

  function firstPanelRowNumber(root) {
    const item = root.querySelector('[data-mega-primary-item][data-has-panel="true"]');
    if (!item) return null;
    return parseInt(item.dataset.megaRow, 10);
  }

  function alignFullWidthDropdown(dropdown) {
    if (!dropdown) return;

    // Reset first so we always measure from the element's true anchor point.
    dropdown.style.setProperty('--mega-shift', '0px');

    const rect = dropdown.getBoundingClientRect();
    const shift = Math.round(rect.left);
    const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);

    dropdown.style.setProperty('--mega-shift', `${shift}px`);
    dropdown.style.setProperty('--mega-scrollbar-width', `${scrollbarWidth}px`);
  }

  function bindFullWidth(root) {
    const dropdown = root.closest('.header__dropdown--mega-full');
    if (!dropdown || dropdown.dataset.megaFullwidthBound === 'true') return;

    dropdown.dataset.megaFullwidthBound = 'true';

    let rafId = null;
    const scheduleAlign = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        alignFullWidthDropdown(dropdown);
      });
    };

    scheduleAlign();
    window.addEventListener('resize', scheduleAlign, { passive: true });
    window.addEventListener('orientationchange', scheduleAlign, { passive: true });
    window.setTimeout(scheduleAlign, 80);
  }

  function bindHoverGuard(root) {
    const disclosure = root.closest('hover-disclosure');
    const dropdown = root.closest('.header__dropdown');
    if (!disclosure || !dropdown || disclosure.dataset.megaHoverGuardBound === 'true') return;

    disclosure.dataset.megaHoverGuardBound = 'true';
    let hideTimer = null;

    function cancelHide() {
      if (hideTimer != null) {
        window.clearTimeout(hideTimer);
        hideTimer = null;
      }
    }

    // Add a small grace period so pointer travel from top nav to mega panel
    // doesn't close the menu if the cursor briefly passes through a gap.
    disclosure.addEventListener(
      'mouseleave',
      (event) => {
        event.stopImmediatePropagation();
        cancelHide();
        hideTimer = window.setTimeout(() => {
          if (disclosure.matches(':hover') || dropdown.matches(':hover')) return;
          disclosure.dispatchEvent(new CustomEvent('theme:disclosure:hide'));
        }, 110);
      },
      true
    );

    disclosure.addEventListener('mouseenter', cancelHide, true);
    dropdown.addEventListener('mouseenter', cancelHide, true);
  }

  function setAdditionalScheme(root, mode) {
    const additional = root.querySelector('[data-mega-additional]');
    if (!additional) return;

    const promoClass = additional.dataset.schemePromo;
    const branchClass = additional.dataset.schemeBranch || promoClass;
    if (!promoClass || !branchClass) return;

    additional.classList.remove(promoClass, branchClass);
    additional.classList.add(mode === 'branch' ? branchClass : promoClass);
  }

  function initHover(root) {
    const collapsePromos = root.dataset.collapsePromos === 'true';
    const defaultIndex = parseInt(root.dataset.defaultIndex || '1', 10);
    let baselineRow = rowHasPanel(root, defaultIndex) ? defaultIndex : firstPanelRowNumber(root);

    if (baselineRow == null) return;

    activateHoverRow(root, baselineRow, collapsePromos);

    findPrimaryItems(root).forEach((item) => {
      const row = parseInt(item.dataset.megaRow, 10);
      const hasPanel = item.dataset.hasPanel === 'true';

      if (!hasPanel) return;

      item.addEventListener('mouseenter', () => activateHoverRow(root, row, collapsePromos));
      item.addEventListener('focusin', () => activateHoverRow(root, row, collapsePromos));

      const trigger = item.querySelector('[data-mega-trigger]');
      if (trigger) {
        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            activateHoverRow(root, row, collapsePromos);
          }
        });
      }
    });

    root.addEventListener('mouseleave', () => {
      activateHoverRow(root, baselineRow, collapsePromos);
    });

    root.addEventListener('focusout', (e) => {
      if (!root.contains(e.relatedTarget)) {
        activateHoverRow(root, baselineRow, collapsePromos);
      }
    });
  }

  function initClick(root) {
    const collapsePromos = root.dataset.collapsePromos === 'true';
    /** @type {number | null} */
    let openRow = null;

    function applyPromoView() {
      openRow = null;
      root.classList.add('mega-menu--state-promo');
      root.classList.remove('mega-menu--state-branch');
      setAdditionalScheme(root, 'promo');
      setLeftActive(root, 0, false);
      findPanelRows(root).forEach((panel) => {
        panel.classList.remove('is-active');
        panel.setAttribute('hidden', '');
      });
      findPrimaryItems(root).forEach((item) => {
        const trigger = item.querySelector('[data-mega-trigger]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
      updateCollapseClass(root, collapsePromos, false, 0);
    }

    function applyBranchView(row) {
      openRow = row;
      root.classList.remove('mega-menu--state-promo');
      root.classList.add('mega-menu--state-branch');
      setAdditionalScheme(root, 'branch');
      setLeftActive(root, row, true);
      setPanelsVisibility(root, row, true);
      updateCollapseClass(root, collapsePromos, true, row);
    }

    applyPromoView();

    root.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-mega-trigger]');
      if (!trigger || !root.contains(trigger)) return;
      e.preventDefault();

      const item = trigger.closest('[data-mega-primary-item]');
      if (!item || item.dataset.hasPanel !== 'true') return;

      const row = parseInt(item.dataset.megaRow, 10);

      if (openRow === row) {
        applyPromoView();
      } else {
        applyBranchView(row);
      }
    });

    root.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      if (openRow != null) {
        e.preventDefault();
        applyPromoView();
      }
    });
  }

  function init(root) {
    if (!root || root[READY]) return;
    root[READY] = true;
    bindFullWidth(root);
    bindHoverGuard(root);

    const interaction = root.dataset.interaction || 'hover';

    if (interaction === 'click') {
      initClick(root);
    } else {
      initHover(root);
    }
  }

  function boot() {
    document.querySelectorAll('[data-mega-menu-root]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
